import { useState, useEffect } from 'react';
import { Menu, X, Sun, Moon } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTheme } from '../contexts/ThemeContext';
import { useLanguage } from '../contexts/LanguageContext';

const navLinks = [
  { key: 'home', href: '#home' },
  { key: 'about', href: '#about' },
  { key: 'skills', href: '#skills' },
  { key: 'projects', href: '#projects' },
  { key: 'certifications', href: '#certifications' },
  { key: 'education', href: '#education' },
  { key: 'contact', href: '#contact' },
];

export default function Navbar() {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [activeSection, setActiveSection] = useState('home');
  const { isDark, toggleTheme } = useTheme();
  const { lang, toggleLang, t } = useLanguage();
  const isNavbarSolid = scrolled || isOpen;

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
      const sections = navLinks.map((l) => l.key);
      for (const id of [...sections].reverse()) {
        const el = document.getElementById(id);
        if (el) {
          const rect = el.getBoundingClientRect();
          if (rect.top <= 120) {
            setActiveSection(id);
            break;
          }
        }
      }
    };

    handleScroll();
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (!isOpen) return undefined;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isOpen]);

  return (
    <motion.nav
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.6, ease: 'easeOut' }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        isNavbarSolid ? 'navbar-scrolled py-3' : 'bg-transparent py-5'
      }`}
    >
      <div className="section-container flex items-center justify-between">
        {/* Logo */}
        <a href="#home" className="group flex items-center gap-2">
          <motion.span
            initial={false}
            animate={{
              opacity: scrolled ? 1 : 0,
              width: scrolled ? 'auto' : '0px',
            }}
            transition={{ duration: 0.25, ease: 'easeOut' }}
            className="overflow-hidden whitespace-nowrap text-heading font-semibold text-sm sm:text-base lg:text-lg"
          >
            <span className="sm:hidden">Restu Agil</span>
            <span className="hidden sm:inline">Restu Agil Yuli Arjun</span>
          </motion.span>
        </a>

        {/* Desktop Navigation */}
        <ul className="hidden lg:flex items-center gap-1">
          {navLinks.map((link) => (
            <li key={link.key}>
              <a
                href={link.href}
                className={`relative px-3 xl:px-4 py-2 rounded-lg text-[13px] xl:text-sm font-medium transition-all duration-300 ${
                  activeSection === link.key
                    ? 'text-heading'
                    : 'text-muted hover:text-heading'
                }`}
              >
                {activeSection === link.key && (
                  <motion.div
                    layoutId="activeNav"
                    className="absolute inset-0 bg-accent-500/10 rounded-lg"
                    transition={{ type: 'spring', bounce: 0.2, duration: 0.6 }}
                  />
                )}
                <span className="relative z-10">{t.nav[link.key]}</span>
              </a>
            </li>
          ))}

          {/* Theme Toggle */}
          <li>
            <button
              onClick={toggleTheme}
              className="p-2 rounded-lg text-muted hover:text-heading transition-colors"
              aria-label="Toggle theme"
            >
              {isDark ? <Sun size={18} /> : <Moon size={18} />}
            </button>
          </li>

          {/* Language Toggle */}
          <li>
            <button
              onClick={toggleLang}
              className="px-2.5 py-1 rounded-lg text-xs font-bold text-muted hover:text-heading transition-colors uppercase tracking-wider"
              aria-label={`${lang} language`}
              title="Toggle language"
            >
              {lang}
            </button>
          </li>

          {/* Hire Me */}
          <li>
            <a
              href="mailto:restuagil.ya@gmail.com"
              className="ml-2 inline-flex items-center justify-center shrink-0 min-w-max whitespace-nowrap px-4 xl:px-5 py-2 rounded-lg bg-linear-to-r from-accent-500 to-cyan-500 text-white text-xs xl:text-sm font-medium hover:shadow-lg hover:shadow-accent-500/25 transition-all duration-300 hover:-translate-y-0.5"
            >
              {t.nav.hireMe}
            </a>
          </li>
        </ul>

        {/* Mobile Controls */}
        <div className="flex lg:hidden items-center gap-2">
          <button
            onClick={toggleTheme}
            className="p-2 rounded-lg text-muted hover:text-heading transition-colors"
            aria-label="Toggle theme"
          >
            {isDark ? <Sun size={18} /> : <Moon size={18} />}
          </button>
          <button
            onClick={toggleLang}
            className="px-2 py-1 rounded-lg text-xs font-bold text-muted hover:text-heading transition-colors uppercase"
            aria-label={`${lang} language`}
            title="Toggle language"
          >
            {lang}
          </button>
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="text-heading p-2 rounded-lg hover:bg-surface transition-colors"
            aria-expanded={isOpen}
            aria-controls="mobile-nav"
            aria-label={isOpen ? 'Close navigation menu' : 'Open navigation menu'}
          >
            {isOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile Navigation */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            id="mobile-nav"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            className="lg:hidden glass mt-2 mx-4 rounded-xl overflow-hidden shadow-xl"
          >
            <ul className="py-4 px-2 space-y-1">
              {navLinks.map((link) => (
                <li key={link.key}>
                  <a
                    href={link.href}
                    onClick={() => setIsOpen(false)}
                    className={`block px-4 py-3 rounded-lg text-sm font-medium transition-all ${
                      activeSection === link.key
                        ? 'text-heading bg-accent-500/10'
                        : 'text-muted hover:text-heading hover:bg-surface'
                    }`}
                  >
                    {t.nav[link.key]}
                  </a>
                </li>
              ))}
              <li className="pt-2 px-2">
                <a
                  href="mailto:restuagil.ya@gmail.com"
                  onClick={() => setIsOpen(false)}
                  className="block text-center px-5 py-3 rounded-lg bg-linear-to-r from-accent-500 to-cyan-500 text-white text-sm font-medium"
                >
                  {t.nav.hireMe}
                </a>
              </li>
            </ul>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.nav>
  );
}
